import * as ExpoFileSystem from 'expo-file-system';
import oneHatData from '@onehat/data';
import getTokenHeaders from '../OneHat/functions/getTokenHeaders';
import getIsMobile from '../OneHat/functions/getIsMobile';

const isMobile = getIsMobile();
let FILES_DIR = '',
	cache;


export function getRemoteUri(fileUri) {
	const UriMaps = oneHatData.getRepository('UriMaps'),
		uriMap = UriMaps.getById(fileUri);
	return uriMap && uriMap.value;
}

export async function setRemoteUri(remoteUri, fileUri) {
	const UriMaps = oneHatData.getRepository('UriMaps');
	await UriMaps.add({
		key: fileUri, 
		value: remoteUri,
	});
}

export async function confirmCacheSystemWorks() {
	if (isMobile) {
		return !!ExpoFileSystem;
	} else {
		// web
		const cacheAvailable = 'caches' in self;
		return !!cacheAvailable;
	}
}

export async function initCacheSystem() {
	if (isMobile) {
		FILES_DIR = ExpoFileSystem.documentDirectory;
	} else {
		// web
		cache = await caches.open('WscInMe');
	}
}

export async function cachedFileExists(path = '') {
	path = FILES_DIR + path;
	if (isMobile) {
		const info = await ExpoFileSystem.getInfoAsync(path);
		return info.exists;
	} else {
		// web
		const fileUri = path,
			remoteUri = getRemoteUri(fileUri);

		const request = new Request(remoteUri),
			response = await cache.match(request);
		return !!response;
	}
}

export async function createCacheSpace(path = '') {
	path = FILES_DIR + path;
	const exists = await cachedFileExists(path);
	if (!exists) {
		if (isMobile) {
			await ExpoFileSystem.makeDirectoryAsync(path, { intermediates: true });
		} else {
			// web
			// do nothing

			// if using a filesystem, recursively build up directory.
		}
	}
}

export async function deleteCachedFiles(path = '') {
	path = FILES_DIR + path;
	const exists = await cachedFileExists(path);
	if (exists) {
		if (isMobile) {
		  
			// cleanup old cached files
			const cachedFiles = await ExpoFileSystem.readDirectoryAsync(path);
		
			let position = 0;
			let results = [];
			const batchSize = 10;
		
			// batching promise.all to avoid exessive promises call
			while (position < cachedFiles.length) {
				const itemsForBatch = cachedFiles.slice(position, position + batchSize);
				results = [...results, ...await Promise.all(itemsForBatch.map(async file => {// eslint-disable-line
					const info = await ExpoFileSystem.getInfoAsync(`${path}${file}`)// eslint-disable-line
					return Promise.resolve({ file, modificationTime: info.modificationTime, size: info.size })
				}))];
				position += batchSize;
			}
		
			// cleanup cache, leave only 5000 most recent files
			const sorted = results
				.sort((a, b) => a.modificationTime - b.modificationTime);
		
			for (let i = 0; sorted.length - i > 8000; i += 1) { // may need to reduce down to 500
				ExpoFileSystem.deleteAsync(`${path}${sorted[i].file}`, { idempotent: true });
			}
			
		} else {
			// web
			const fileUri = path,
				remoteUri = getRemoteUri(fileUri);

			const request = new Request(remoteUri);
			cache.delete(request);
		}
		await createCacheSpace(path);
	}
}


export async function getCachedFile(path = '') {
	const exists = await cachedFileExists(path);
	if (exists) {
		path = FILES_DIR + path;
		if (isMobile) {
			
			return path;

		} else {
			// web
			const fileUri = path,
				remoteUri = getRemoteUri(fileUri);

			const request = new Request(remoteUri);
			const response = await cache.match(request);
			const blob = await response.blob();
			const reader = new FileReader();
			let base64 = await new Promise((resolve, reject) => {
				reader.readAsDataURL(blob); 
				reader.onloadend = function() {
					resolve(reader.result);
				}
			});
			base64 = base64.replace('data:application/octet-stream', 'data:audio/mp3');
			return base64;
		}
	}
}

/**
 * Downloads and caches the file for future use.
 * @param {*} remoteUri Where to get the file
 * @param {*} fileUri How the file will be referenced from now on
 * @param {*} progressCallback If given, will be called with progress updates on the download
 * Matches signature ({ totalBytesWritten, totalBytesExpectedToWrite }) => {}
 */
export async function downloadAndCacheFile(remoteUri, fileUri, progressCallback = null) {
	let exists = await cachedFileExists(fileUri);
	if (!exists) {
		if (isMobile) {
			const headers = getTokenHeaders();
			let downloadedFile;
			if (progressCallback) {
				// progressCallback is of signature: ({ totalBytesWritten, totalBytesExpectedToWrite }) => {}
				const downloadResumable = ExpoFileSystem.createDownloadResumable(remoteUri, FILES_DIR + fileUri, { headers }, progressCallback);
				downloadedFile = await downloadResumable.downloadAsync();
			} else {
				downloadedFile = await ExpoFileSystem.downloadAsync(remoteUri, FILES_DIR + fileUri, {
					headers,
					sessionType: ExpoFileSystem.FileSystemSessionType.FOREGROUND,
				})
			}
			if (downloadedFile.status !== 200) {
				throw new Error(downloadedFile);
			}
		} else {
			// web
			let response = await fetch(remoteUri);
			if (!response.ok) {
				throw new Error(response);
			}

			const reader = response.body.getReader(),
				contentLength = +response.headers.get('Content-Length');
			let receivedLength = 0,
				chunks = []; // array of received binary chunks (comprises the body)
			while(true) {
				const { done, value } = await reader.read();
				if (done) {
					break;
				}
				chunks.push(value);
				receivedLength += value.length;
				if (progressCallback) {
					progressCallback({ totalBytesWritten: receivedLength, totalBytesExpectedToWrite: contentLength });
				}
			}

			// Need to create a new response, since we've already read the one above
			let chunksAll = new Uint8Array(receivedLength); // (4.1)
			let position = 0,
				chunk;
			for(chunk of chunks) {
				chunksAll.set(chunk, position); // (4.2)
				position += chunk.length;
			}
			response = new Response(chunksAll);
			cache.put(remoteUri, response);

			await setRemoteUri(remoteUri, fileUri);
		}
		
		exists = await cachedFileExists(fileUri);
		if (!exists) {
			throw Error('Could not download file');
		}
	}
}

export async function createCorruptedFile(path = '') {
	path = FILES_DIR + path;
	await deleteCachedFiles(path);
	await createCacheSpace(path);
	if (isMobile) {
		const contents = 'corrupted file content';
		await ExpoFileSystem.writeAsStringAsync(path, contents, { encoding: ExpoFileSystem.EncodingType.Base64, });
	} else {
		// web
		// do nothing


	}
}
